import { useEffect, useRef } from "react";
import { useNotifications } from "../context/NotificationContext";
import { useClient } from "../context/ClientContext";

/**
 * Global listener that keeps ClientContext in sync with `transaction.sync`
 * Pusher events emitted by the 4 client-mutating backend endpoints
 * (create_clients, edit_clients, delete_client, merge_clients).
 *
 * - created / updated: refetch the single client (getClient) and merge into
 *   the list — cost is one row per event, independent of salon size.
 * - deleted: evict the client from the list by uuid.
 *
 * Cursors on SyncEvent.seq (monotonic id stamped by NotificationContext)
 * instead of pusherSyncEvents.length, which plateaus once the rolling
 * 50-event buffer fills — the length-based cursor would silently swallow
 * every subsequent event for the tab's lifetime (this hook mounts once at
 * AppContent for the whole session). Ref is lazy-initialized to the last
 * seq already in the backlog on first mount so events the initial fetch
 * already reflects are not re-processed.
 */
export function useClientSyncListener(): void {
  const { pusherSyncEvents } = useNotifications();
  const { refreshClientById, evictClientById } = useClient();
  const lastSeqRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastSeqRef.current === null) {
      lastSeqRef.current =
        pusherSyncEvents.length > 0
          ? pusherSyncEvents[pusherSyncEvents.length - 1].seq
          : 0;
      return;
    }
    const cursor = lastSeqRef.current;
    const newEvents = pusherSyncEvents.filter((ev) => ev.seq > cursor);
    if (newEvents.length === 0) return;
    lastSeqRef.current = newEvents[newEvents.length - 1].seq;

    for (const ev of newEvents) {
      if (ev?.entity !== "client" || !ev.target_id) continue;
      if (ev.action === "deleted") {
        evictClientById(ev.target_id);
      } else {
        void refreshClientById(ev.target_id);
      }
    }
  }, [pusherSyncEvents, refreshClientById, evictClientById]);
}
